import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import SpaceService, { SpaceProps } from 'services/hooks/space-service';
import { Table } from '../Common/Table';
import Delete from './SpaceForms/delete';
import Save from './SpaceForms/save';
import { useAuth } from 'services/auth-service';

export default function SpaceSaveMode() {
  const [getSpacesResult, setGetSpacesResult] = useState<SpaceProps[] | null>([]);
  const {
    isLoading: isLoadingSpaces,
    refetch,
    data: spaceData
  } = useQuery<SpaceProps[], Error>('spaces', SpaceService.findAllSpaces, {
    onError: (err: Error) => {
      if (err instanceof AxiosError) {
        console.log(err.response?.data || err);
      } else {
        console.error(err);
      }
    }
  });

  useEffect(() => {
    if (spaceData && Array.isArray(spaceData)) {
      refetch();
      setGetSpacesResult(spaceData);
    }
  }, [spaceData, refetch]);

  const {
    state: { user }
  } = useAuth();

  interface Column {
    id: string;
    Header: string;
    accessor: any;
    disableFilters: boolean;
    Cell?: any;
    width?: number;
  }

  const columns = useMemo(
    () => {
      const baseColumns: Column[] = [
        {
          id: 'name',
          Header: 'Name',
          accessor: 'name',
          disableFilters: true
        },
        {
          id: 'description',
          Header: 'Description',
          accessor: 'description',
          disableFilters: true
        },
        {
          id: 'squareFeet',
          Header: 'Square Feet',
          accessor: 'squareFeet',
          disableFilters: true
        },
        {
          id: 'capacity',
          Header: 'Capacity',
          accessor: 'capacity',
          disableFilters: true
        }
      ];

      // Show the Actions column only if the logged-in user is a SUPER user
      if (user.role === 'SUPER') {
        baseColumns.push({
          id: 'actions',
          Header: 'Actions',
          accessor: (row: SpaceProps) => row,
          Cell: (row: {
            value: SpaceProps;
            setEditUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
            setUpdateShowModal: React.Dispatch<React.SetStateAction<boolean>>;
            setDeleteUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
            setDeleteShowModal: React.Dispatch<React.SetStateAction<boolean>>;
          }) => {
            return (
              <div className="flex gap-4">
                <SecondaryButton
                  onClick={() => {
                    row.setEditUUID(row.value.uuid);
                    row.setUpdateShowModal(true);
                  }}
                  buttonLabel="Edit"
                  variant="default"
                  size="px-4 w-auto"
                />
                <SecondaryButton
                  onClick={() => {
                    row.setDeleteUUID(row.value.uuid);
                    row.setDeleteShowModal(true);
                  }}
                  buttonLabel="Delete"
                  variant="default"
                  size="px-4 w-auto"
                />
              </div>
            );
          },
          width: 150,
          disableFilters: true
        });
      }

      return baseColumns;
    },
    [user.role] // Depending on user role, recompute columns when role changes
  );

  if (isLoadingSpaces || !getSpacesResult) {
    return <LoadingSpinner />;
  }

  return (
    <Table
      name="Manage Spaces"
      refetch={refetch}
      columns={columns}
      data={getSpacesResult}
      CreateForm={Save}
      DeleteForm={Delete}
      createQuery={SpaceService.createSpace}
      updateQuery={SpaceService.updateSpace}
      deleteQuery={SpaceService.deleteSpaceById}
      editQuery={SpaceService.findSpaceById}
      queryName="spaces"
      buttonName="Space"
    />
  );
}
